// lib/gameStorage.js
import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp, onSnapshot,
  collection, addDoc, query, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

// Giriş yapıldığında gün sayısı (Streak) ve Profil Kontrolü
export async function ensureUserProfile(firebaseUser) {
  const ref = doc(db, 'users', firebaseUser.uid);
  const snap = await getDoc(ref);
  const todayStr = new Date().toISOString().split('T')[0]; // "YYYY-MM-DD"

  if (!snap.exists()) {
    // İlk Kez Oluşturulan Profil
    await setDoc(ref, {
      nickname: firebaseUser.displayName || 'Oyuncu',
      email: firebaseUser.email || '',
      photoURL: firebaseUser.photoURL || '',
      nicknameChanged: false,
      gamesPlayed: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      score: 0,            // Toplam Puan
      streak: 1,           // Giriş Serisi
      lastLoginDate: todayStr,
      ribbons: [],         // Kazanılan Görev Şeritleri / Rozetler
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else {
    // Mevcut Kullanıcı için Günlük Streak Kontrolü
    const data = snap.data();
    const lastDate = data.lastLoginDate;

    if (lastDate !== todayStr) {
      let newStreak = 1;
      if (lastDate) {
        const last = new Date(lastDate);
        const today = new Date(todayStr);
        const diffTime = Math.abs(today - last);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) {
          // Aralıksız bir sonraki gün girmiş
          newStreak = (data.streak || 0) + 1;
        } else if (diffDays > 1) {
          // Gün atlamış, seri sıfırlanır
          newStreak = 1;
        }
      }

      const updatedRibbons = checkStreakRibbons(data.ribbons || [], newStreak);

      await updateDoc(ref, {
        streak: newStreak,
        lastLoginDate: todayStr,
        ribbons: updatedRibbons,
        updatedAt: serverTimestamp(),
      });
    }
  }
}

// Görev Şeritleri Kontrol Mantığı
function checkStreakRibbons(currentRibbons, streak) {
  const newRibbons = [...currentRibbons];
  if (streak >= 10 && !newRibbons.some(r => r.id === 'streak_10')) {
    newRibbons.push({ id: 'streak_10', title: '10 Günlük Seri', icon: '🔥', desc: '10 gün aralıksız giriş yapıldı.' });
  }
  return newRibbons;
}

function checkGameRibbons(currentRibbons, gamesPlayed, wins) {
  const newRibbons = [...currentRibbons];
  if (gamesPlayed >= 3 && !newRibbons.some(r => r.id === 'games_3')) {
    newRibbons.push({ id: 'games_3', title: 'Çaylak Oyuncu', icon: '♟️', desc: '3 maç tamamlandı.' });
  }
  if (wins >= 5 && !newRibbons.some(r => r.id === 'wins_5')) {
    newRibbons.push({ id: 'wins_5', title: 'Usta Ustası', icon: '🏆', desc: '5 galibiyet elde edildi.' });
  }
  return newRibbons;
}

export function subscribeUserProfile(uid, callback) {
  const ref = doc(db, 'users', uid);
  return onSnapshot(ref, (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export async function changeNickname(uid, newNickname) {
  const trimmed = (newNickname || '').trim();
  if (!trimmed) throw new Error('Takma ad boş olamaz.');
  if (trimmed.length > 30) throw new Error('Takma ad en fazla 30 karakter olabilir.');

  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Profil bulunamadı.');
  if (snap.data().nicknameChanged) {
    throw new Error('Takma adını sadece bir kez değiştirebilirsin.');
  }
  await updateDoc(ref, {
    nickname: trimmed,
    nicknameChanged: true,
    updatedAt: serverTimestamp(),
  });
}

// Oyun Sonucunu Kaydetme (Puan Hesaplama: Galibiyet +2, Beraberlik +1, Mağlubiyet +0)
// lib/gameStorage.js

export async function recordGameResult(uid, { result, reason, history, opponentNickname, isMultiplayer = false, aiLevel = null }) {
  const userRef = doc(db, 'users', uid);
  const gamesRef = collection(db, 'users', uid, 'games');

  await addDoc(gamesRef, {
    result,                 // 'win' | 'loss' | 'draw'
    reason: reason || null,
    moveCount: history.length,
    history: history || [],
    opponentNickname: opponentNickname || 'Bilgisayar',
    isMultiplayer,
    aiLevel,
    playedAt: serverTimestamp(),
  });

  const snap = await getDoc(userRef);
  const data = snap.exists() ? snap.data() : { gamesPlayed: 0, wins: 0, draws: 0, losses: 0, score: 0, ribbons: [] };

  const newGamesPlayed = (data.gamesPlayed || 0) + 1;
  const newWins = (data.wins || 0) + (result === 'win' ? 1 : 0);
  const newDraws = (data.draws || 0) + (result === 'draw' ? 1 : 0);
  const newLosses = (data.losses || 0) + (result === 'loss' ? 1 : 0);

  // 🎯 PUAN HESAPLAMA LOGIĞI
  let pointsEarned = 0;
  if (isMultiplayer) {
    // ⚔️ Arena / Multiplayer Maç Puanları
    if (result === 'win') pointsEarned = 5;
    else if (result === 'draw') pointsEarned = 1;
    else if (result === 'loss') pointsEarned = -2;
  } else {
    // 🤖 Yapay Zekaya Karşı Maç Puanları — galibiyet puanı zorluğa göre artar
    const AI_WIN_POINTS = { easy: 1, medium: 2, hard: 3, expert: 4 };
    if (result === 'win') pointsEarned = AI_WIN_POINTS[aiLevel] || 2;
    else if (result === 'draw') pointsEarned = 1;
    else if (result === 'loss') pointsEarned = 0;
  }

  // Puan eksiye düşmesin (Min: 0)
  const currentScore = data.score || 0;
  const newScore = Math.max(0, currentScore + pointsEarned);

  const updatedRibbons = checkAllRibbons(
    data.ribbons || [],
    data.streak || 1,
    newGamesPlayed,
    newWins
  );

  await updateDoc(userRef, {
    gamesPlayed: newGamesPlayed,
    wins: newWins,
    draws: newDraws,
    losses: newLosses,
    score: newScore,
    ribbons: updatedRibbons,
    updatedAt: serverTimestamp(),
  });
}

export async function fetchUserGames(uid, max = 20) {
  const gamesRef = collection(db, 'users', uid, 'games');
  const q = query(gamesRef, orderBy('playedAt', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Liderlik Tablosunu Puan 'score' Değerine Göre Sıralama
export async function fetchLeaderboard(max = 50) {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, orderBy('score', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// lib/gameStorage.js içindeki rozet kontrolü güncellendi
function checkAllRibbons(currentRibbons, streak, gamesPlayed, wins, mpGames = 0) {
  const currentSet = new Set(currentRibbons.map((r) => r.id));

  const addIfEligible = (id) => {
    if (!currentSet.has(id)) {
      currentSet.add(id);
    }
  };

  // Streak kontrolleri
  if (streak >= 1) addIfEligible('streak_1');
  if (streak >= 3) addIfEligible('streak_3');
  if (streak >= 7) addIfEligible('streak_7');
  if (streak >= 10) addIfEligible('streak_10');
  if (streak >= 25) addIfEligible('streak_25');
  if (streak >= 50) addIfEligible('streak_50');

  // Maç sayısı kontrolleri
  if (gamesPlayed >= 1) addIfEligible('games_1');
  if (gamesPlayed >= 3) addIfEligible('games_3');
  if (gamesPlayed >= 10) addIfEligible('games_10');
  if (gamesPlayed >= 25) addIfEligible('games_25');
  if (gamesPlayed >= 50) addIfEligible('games_50');
  if (gamesPlayed >= 100) addIfEligible('games_100');

  // Galibiyet kontrolleri
  if (wins >= 1) addIfEligible('wins_1');
  if (wins >= 3) addIfEligible('wins_3');
  if (wins >= 5) addIfEligible('wins_5');
  if (wins >= 10) addIfEligible('wins_10');
  if (wins >= 25) addIfEligible('wins_25');

  // Multiplayer kontrolleri
  if (mpGames >= 1) addIfEligible('mp_1');
  if (mpGames >= 3) addIfEligible('mp_3');
  if (mpGames >= 10) addIfEligible('mp_10');

  return Array.from(currentSet).map((id) => ({ id }));
}